// singlePlanCheckoutConfig.ts
import locales from '../content.json';
import { PAYMENT_TARIFF } from '../types';
import { Environment } from '../types/environment';
import { getParameterFromURL } from '../utils/scripts';
import { getContentIds } from '../utils/scripts/getContentIds';

type SupportedLang = 'en' | 'ru' | 'es' | 'fr' | 'de' | 'pt' | 'it' | 'tr' | 'pl' | 'uk';
const SUPPORTED_LANGS: SupportedLang[] = ['en', 'ru', 'es', 'fr', 'de', 'pt', 'it', 'tr', 'pl', 'uk'];

type Period = 'year' | 'sixMonths' | 'threeMonths' | 'month' | 'week';

type Region = {
  subscrUK: boolean;
  subscrEU: boolean;
};

type ProductIds = Partial<Record<Period, number>>;

type Money = string;
type PriceNumber = number;

type PlanConfig = {
  id: number;
  period: Period;
  title: string;

  price: Money;
  perDay?: PriceNumber;
  perWeek?: PriceNumber;

  oldPrice?: Money;
  oldPerDay?: Money | PriceNumber;
  oldForPeriod?: Money | PriceNumber;

  productName?: string;
};

type SinglePlanCheckoutConfig = {
  lang: SupportedLang;
  currencySymbol: '£' | '€' | '$';
  subscrUK: boolean;
  subscrEU: boolean;

  mostPopularTitle: string;
  plan: PlanConfig;
};

type TariffData = {
  ids: (env: Environment, region: Region) => ProductIds;
  prices: Partial<Record<Period, Money>>;
  perDay?: Partial<Record<Period, number>>;
  perWeek?: Partial<Record<Period, number>>;
  productNameTemplate?: Partial<Record<Period, string>>;
};

function resolveLang(): SupportedLang {
  const raw = (window?.navigator?.language?.slice(0, 2) || 'en') as SupportedLang;
  return SUPPORTED_LANGS.includes(raw) ? raw : 'en';
}

function resolveRegion() {
  const subscrUK = getParameterFromURL('subscrUK') === 'true';
  const subscrEU = getParameterFromURL('subscrEU') === 'true';
  const currencySymbol: '£' | '€' | '$' = subscrUK ? '£' : subscrEU ? '€' : '$';
  return { subscrUK, subscrEU, currencySymbol };
}

function normalizePeriod(period: string | undefined): Period {
  switch (period) {
    case 'year':
    case 'sixMonths':
    case 'threeMonths':
    case 'month':
    case 'week':
      return period;
    default:
      return 'month';
  }
}

const TARIFF_MAP: Partial<Record<PAYMENT_TARIFF, TariffData>> = {
  [PAYMENT_TARIFF.WINBACK_V1]: {
    ids: (env, { subscrUK, subscrEU }) => {
      if (subscrUK) {
        return {
          month: env === Environment.prod ? 117 : 122,
        };
      }
      if (subscrEU) {
        return {
          month: env === Environment.prod ? 118 : 123,
        };
      }
      return {
        month: env === Environment.prod ? 116 : 121,
      };
    },
    prices: { month: '1.99' },
    perDay: { month: 0.06 },
    perWeek: { month: 0.46 },
    productNameTemplate: { month: '{{currency}}1.99 per 1m intro, then {{currency}}29.99 per 1 month' },
  },
};

const FALLBACK_TARIFF: TariffData = {
  ids: (env, { subscrUK, subscrEU }) => {
    if (subscrUK) {
      return {
        month: env === Environment.prod ? 117 : 122,
      };
    }
    if (subscrEU) {
      return {
        month: env === Environment.prod ? 118 : 123,
      };
    }
    return {
      month: env === Environment.prod ? 116 : 121,
    };
  },
  prices: { month: '1.99' },
  perDay: { month: 0.06 },
  perWeek: { month: 0.46 },
  productNameTemplate: { month: '{{currency}}1.99 per 1m intro, then {{currency}}29.99 per 1 month' },
};

function getPeriodTexts(lang: SupportedLang) {
  const l = locales[lang];

  return {
    mostPopularTitle: l.tariff_most_popular,

    year: {
      title: l.tariff_one_year,
      perFirst: l.tariff_per_year,
      perSecond: l.tariff_per_day,
    },
    month: {
      title: l.tariff_one_month,
      perFirst: l.tariff_month,
      perSecond: l.tariff_per_day,
      perWeek: l.tariff_per_week,
    },
    week: {
      title: l.tariff_one_week,
      perFirst: l.tariff_week,
      perSecond: l.tariff_per_day,
    },
    threeMonths: {
      title: l.tariff_three_month,
      perSecond: l.tariff_per_day,
      perWeek: l.tariff_per_week,
    },
    sixMonths: {
      title: l.tariff_six_month,
      perSecond: l.tariff_per_day,
      perWeek: l.tariff_per_week,
    },
  };
}

export function getSinglePlanCheckoutConfig(params: {
  tariff: PAYMENT_TARIFF;
  period?: Period | string;
  environment?: Environment | string;
  sendContentIds?: boolean;
}): SinglePlanCheckoutConfig {
  const lang = resolveLang();
  const { subscrUK, subscrEU, currencySymbol } = resolveRegion();

  const environment =
    (params.environment as Environment) || (process.env.ENVIRONMENT as Environment) || Environment.prod;

  const period = normalizePeriod(typeof params.period === 'string' ? params.period : params.period);

  const tariffData = TARIFF_MAP[params.tariff] ?? FALLBACK_TARIFF;

  const region: Region = { subscrUK, subscrEU };
  const ids = tariffData.ids(environment, region);

  const id = ids[period];
  const price = tariffData.prices[period];

  if (!id || price == null) {
    throw new Error(`[singlePlanCheckout] Missing config for tariff=${String(params.tariff)} period=${period}`);
  }

  if (params.sendContentIds) {
    getContentIds({ [period]: id });
  }

  const texts = getPeriodTexts(lang);
  const textForPeriod = texts[period];

  const productNameTemplate = tariffData.productNameTemplate?.[period];
  const productName = productNameTemplate
    ? productNameTemplate.split('{{currency}}').join(currencySymbol)
    : undefined;

  return {
    lang,
    currencySymbol,
    subscrUK,
    subscrEU,
    mostPopularTitle: texts.mostPopularTitle,

    plan: {
      id,
      period,
      title: textForPeriod.title,
      price,
      perDay: tariffData.perDay?.[period],
      perWeek: tariffData.perWeek?.[period],
      productName,
    },
  };
}